'use client';

import { useEffect } from 'react';
import { CoreProvider, Popup } from '@weareconceptstudio/core';
import { FormProvider } from '@weareconceptstudio/form';
import { AccountProvider } from '@weareconceptstudio/account';
import { UserProvider } from '@weareconceptstudio/account/user';
import { CatalogProvider } from '@weareconceptstudio/ecommerce/v1';
import ProductPopupWithTracking from '@/components/ProductPopupWithTracking';
import { DataProvider, UIProvider } from '@/context';
import { config } from '@/helpers';
import translations from '@/translations';
import Image from '@/components/Image';
import Header from '@/components/Header';
import BurgerMenu from '@/components/BurgerMenu';
import CustomLink from '@/components/CustomLink';
import HighOrderComponent from '@/components/HighOrderComponent';
import AddressField from '@/components/AddressField';

import AddressMapField from '@/components/AddressMapField';
import SubscribePopup from '@/components/SubscribePopup';
import ExclusiveBenefits from '@/components/ExclusiveBenefits';
import MenuProductTracking from '@/components/MenuProductTracking';
import CartTracking from '@/components/CartTracking';
import PageLoadingOverlay from '@/components/PageLoadingOverlay';
import FulfillmentCheckoutInfo from '@/modules/Checkout/FulfillmentCheckoutInfo';
import theme from '@/styles/theme';
import { createMurakamiUserMethods, murakamiUserHooks } from '@/libs/murakami-user-methods';
import { CHUNK_RELOAD_KEY } from '@/libs/chunk-load-error';

const popupCloseIcon = (
	<svg
		xmlns='http://www.w3.org/2000/svg'
		width='16'
		height='16'
		viewBox='0 0 16 16'
		fill='black'>
		<path d='M7.97888 6.50915L1.46973 0L-8.13653e-05 1.46981L6.50907 7.97896L7.97888 6.50915Z' />
		<path d='M9.40418 7.98229L15.9133 1.47314L14.4435 0.0033361L7.93437 6.51249L9.40418 7.98229Z' />
		<path d='M7.97888 9.49085L1.46973 16L-8.13653e-05 14.5302L6.50907 8.02104L7.97888 9.49085Z' />
		<path d='M9.40418 8.01771L15.9133 14.5269L14.4435 15.9967L7.93437 9.48751L9.40418 8.01771Z' />
	</svg>
);

const selectDropdownIcon = (
	<svg
		viewBox='0 0 24 24'
		xmlns='http://www.w3.org/2000/svg'>
		<g>
			<g id='Group 28934'>
				<path d='M12.0157 15.0118L18.5918 8.43567L20.0767 9.92059L13.5006 16.4967L12.0157 15.0118Z' />
				<path d='M10.5766 16.5L4.00049 9.92395L5.48541 8.43903L12.0615 15.0151L10.5766 16.5Z' />
			</g>
		</g>
	</svg>
);

const userProviderProps = {
	routes: config.routes,
	userIntegration: {
		defaultLocale: 'hy',
		endpoints: {
			currentUser: 'current-user',
			updateAccount: 'update-account',
			updatePassword: 'update-password',
			logout: 'logout',
		},
		verificationRules: {
			phone: {
				isVerified: (user) => !!user?.phone && !!user?.phone_verified_at,
				redirectRouteKey: 'verifyPhone',
			},
		},
		verificationGuard: {
			enabled: true,
		},
	},
	hooks: murakamiUserHooks,
	createMethods: createMurakamiUserMethods,
};

export default function AppProviders({ children, lang, initialUser, globalData }) {
	useEffect(() => {
		const timeout = window.setTimeout(() => {
			try {
				sessionStorage.removeItem(CHUNK_RELOAD_KEY);
			} catch {
				// Private browsing can block sessionStorage.
			}
		}, 15000);

		return () => window.clearTimeout(timeout);
	}, []);

	const addressFormFields = [
		{
			fieldType: 'custom',
			field: <AddressField />,
		},
		{
			fieldType: 'input',
			labelProps: {
				label: 'entrance',
				name: 'entrance',
				required: false,
				className: 'input-line',
			},
			fieldProps: {
				placeholder: 'entrancePlaceholder',
			},
		},

		{
			fieldType: 'input',
			labelProps: {
				label: 'floor',
				name: 'floor',
				required: false,
				className: 'input-line',
			},
			fieldProps: {
				placeholder: 'floorPlaceholder',
			},
		},
		{
			fieldType: 'input',
			labelProps: {
				label: 'apartment',
				name: 'apartment',
				required: false,
				className: 'input-line',
			},
			fieldProps: {
				placeholder: 'apartmentPlaceholder',
			},
		},
		{
			fieldType: 'input',
			labelProps: {
				label: 'doorBell',
				name: 'door_bell',
				required: false,
				className: 'input-line',
			},
			fieldProps: {
				placeholder: 'doorBellPlaceholder',
			},
		},
		{
			fieldType: 'input',
			labelProps: {
				label: 'addressName',
				name: 'name',
			},
			fieldProps: {
				placeholder: 'addressNamePlaceholder',
			},
		},
		{
			fieldType: 'phone',
			labelProps: {
				label: 'phone',
				name: 'phone',
			},
		},
		{
			fieldType: 'textarea',
			labelProps: {
				label: 'noteForCourier',
				name: 'note_for_courier',
				required: false,
			},
			fieldProps: {
				placeholder: 'noteForCourierPlaceholder',
			},
		},
	];

	return (
		<CoreProvider
			theme={theme}
			language={lang}
			iconPrefix='murakami'
			translations={translations}
			components={{
				link: <CustomLink />,
				image: <Image />,
			}}>
			<UIProvider>
				<UserProvider
					{...userProviderProps}
					initialUser={initialUser}>
					<DataProvider
						newData={{
							globalData: {
								[lang]: globalData,
							},
						}}>
						<FormProvider
							fontFamily='montserrat_arm'
							selectIcon={selectDropdownIcon}>
							<AccountProvider
								currency='AMD'
								productUrlPrefix={false}
								fontFamily='montserrat_arm'
								productPopup={ProductPopupWithTracking}
								shopUrl={config.routes.menu.path}
								checkout={{
									FulfillmentReviewComponent: FulfillmentCheckoutInfo,
								}}
								fulfillment={
									globalData.fulfillment
										? {
												...globalData.fulfillment,
												types: ['delivery', 'takeaway'],
											}
										: null
								}
								addressFormSidePanel={<AddressMapField />}
								addressFormFields={addressFormFields}>
								<CatalogProvider fontFamily='montserrat_arm'>
									<Header
										phone={globalData.phones}
										text={globalData.delivery}
									/>

									<HighOrderComponent lang={lang}>{children}</HighOrderComponent>

									{!globalData.new_year ? <MenuProductTracking /> : null}
									{!globalData.new_year ? <CartTracking /> : null}

									<BurgerMenu menuBar={globalData.menu_bar} />

									<SubscribePopup />

									<ExclusiveBenefits />

									<Popup closeIcon={popupCloseIcon} />

									<PageLoadingOverlay />
								</CatalogProvider>
							</AccountProvider>
						</FormProvider>
					</DataProvider>
				</UserProvider>
			</UIProvider>
		</CoreProvider>
	);
}
